import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { Navbar as BootstrapNavbar, Nav, Container } from 'react-bootstrap';
import logoImage from '../images/offplanner-logo-white.png'; // Import the logo image

/**
 * Navigation component for the Off Planner application
 * Displays different options based on authentication status
 */
const Navbar = ({ currentUser }) => {
  const navigate = useNavigate();
  
  // State to track if the navbar is expanded
  const [expanded, setExpanded] = useState(false);
  
  // Ref to store the timeout ID
  const timeoutRef = useRef(null);

  // Handle user logout
  const handleLogout = async () => {
    try {
      // Close the navbar before navigating
      setExpanded(false);
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };
  
  // Function to close the navbar
  const closeNavbar = () => {
    setExpanded(false);
  };
  
  // Set up a timer to auto-collapse the navbar after 30 seconds of inactivity
  useEffect(() => {
    // Clear any existing timeout when expanded state changes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Only set a timeout if the navbar is expanded
    if (expanded) {
      // Auto-collapse after 30 seconds (30000 ms)
      timeoutRef.current = setTimeout(() => {
        setExpanded(false);
      }, 30000);
    }
    
    // Clean up the timeout when the component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [expanded]);

  return (
    <BootstrapNavbar 
      bg="dark" 
      variant="dark" 
      expand="lg" 
      expanded={expanded} 
      onToggle={(isExpanded) => setExpanded(isExpanded)}
    >
      <Container>
        <BootstrapNavbar.Brand as={Link} to="/" onClick={closeNavbar}>
          <img 
            src={logoImage} 
            alt="OFF PLANNER" 
            height="30" 
            className="d-inline-block align-top"
          />
        </BootstrapNavbar.Brand>
        <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />
        <BootstrapNavbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            {/* Add onClick={closeNavbar} to each Nav.Link to close when clicked */}
            {currentUser ? (
              <>
                <Nav.Link as={Link} to="/manage-friends" onClick={closeNavbar}>Manage Friends</Nav.Link>
                <Nav.Link as={Link} to="/profile" onClick={closeNavbar}>Profile</Nav.Link>
                {/* Only show admin link if email contains 'admin' - in a real app, use proper role-based checks */}
                {currentUser.email && currentUser.email.includes('admin') && (
                  <Nav.Link as={Link} to="/admin" onClick={closeNavbar}>Admin Panel</Nav.Link>
                )}
                <Nav.Link onClick={handleLogout}>Logout</Nav.Link>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/login" onClick={closeNavbar}>Login</Nav.Link>
                <Nav.Link as={Link} to="/register" onClick={closeNavbar}>Register</Nav.Link>
              </>
            )}
          </Nav>
        </BootstrapNavbar.Collapse>
      </Container>
    </BootstrapNavbar>
  );
};

export default Navbar;
